/* eslint-disable @typescript-eslint/no-explicit-any */
import { extend } from 'umi-request';
import assign from 'lodash/assign';
import { history } from 'umi';
import Aegis from 'aegis-web-sdk';
import type { ResponseError } from 'umi-request/types';
import { stringify } from 'qs';
import {
  getErrorMsg,
  camelCase,
  toUnderline,
  GlobalResponseError,
  onErrorNotice,
} from '@bluevision/tools';

import type { Request } from '@bluevision/tools';
import { aegis } from '@/global';
import i18n from '@/i18n';
import { noLoginRequired } from '..';
import posthog from 'posthog-js';

const defaultError: string = '未知错误';

// 都是国际化翻译文案的text_id
const codeMessage = {
  200: 'the_server_successfully_returned_the_requested_data',
  201: 'successfully_created_or_modified_data',
  202: 'a_request_has_entered_the_backend_queue_asynchronous_task',
  204: 'successfully_deleted_data',
  400: 'the_request_sent_has_an_error_and_the_server_did_not_perform_any_new_or_modified_data_operations',
  401: 'the_user_does_not_have_permission_incorrect_token_username_password',
  403: 'the_user_is_authorized_but_access_is_prohibited',
  404: 'the_request_was_made_for_a_non_existent_record',
  406: 'the_requested_format_is_not_available',
  410: 'the_requested_resource_has_been_permanently_deleted_and_will_no_longer_be_obtained',
  422: 'a_validation_error_occurred_when_creating_an_object',
  500: 'the_server_encountered_an_error_please_check_the_server',
  502: 'gateway_error',
  503: 'service_unavailable_server_temporarily_overloaded_or_under_maintenance',
  504: 'gateway_timeout',
};

export const errorHandler = (error: ResponseError): void => {
  const { data, response } = error;
  if (response) {
    const { status, statusText } = response;
    const title = i18n.t(codeMessage[status]) ?? statusText ?? defaultError;
    const errorData = data && typeof data === 'object' ? camelCase(data) : title;
    if (status === 401) {
      // 重新登录后跳转到当前 URL
      if (!noLoginRequired()) {
        history.replace({
          pathname: '/user/login',
          search: stringify({ redirect: window.location.href }),
        });
      }
    } else if (status === 400 || status === 404) {
      throw new GlobalResponseError(errorData, data?.info ?? title);
    } else if (response.status >= 500) {
      throw new GlobalResponseError(errorData, title);
    } else {
      throw new GlobalResponseError(errorData, title);
    }
  } else {
    throw new GlobalResponseError(null, defaultError);
  }
};

// extend 新建一个 umi-request 实例
const umiRequest = extend({
  errorHandler, // 默认错误处理
  credentials: 'include', // 默认请求是否带上cookie
  timeout: 1000 * 60 * 5,
  paramsSerializer: (p: unknown) => stringify(p, { arrayFormat: 'comma' }),
});

// request拦截器, 改变url 或 options.
umiRequest.interceptors.request.use((url, { data, params, isLower = true, ...r }) => ({
  url,
  options: {
    ...r,
    data: isLower ? toUnderline(data) : data,
    params: isLower ? toUnderline(params) : params,
  },
}));

const request: Request = async (url, options) => {
  const { notice, isCamel = true, getResponse, includeResponse, ...r }: any = options;
  try {
    // includeResponse：打开 umi-request 的 getResponse 以拿到 Fetch Response（含响应头，如 Content-Disposition）
    const response: any = await umiRequest(url, includeResponse ? { ...r, getResponse: true } : r);

    // includeResponse 时 umi-request 返回 { data, response }，原样透传给调用方自行取头 / 取体
    if (includeResponse) {
      return [response, null];
    }

    const requestData = options?.data?.password
      ? assign({}, options?.data, { password: '*'.repeat(options?.data?.password.length) })
      : options?.data; // 对 password 进行脱敏，替换为同长度的 '*' 字符串
    const ext1: Record<string, any> = {
      url,
      method: options?.method,
      headers: options?.headers,
      params: options?.params,
      data: requestData,
    };
    const ext2: Record<string, any> = {
      success: response?.success,
      request_id: response?.request_id,
      info: response?.info,
      data: response?.data,
    };

    // 自定义上报接口异常的信息，参考：https://cloud.tencent.com/document/product/1464/58563#info.E3.80.81infoall.E3.80.81error-.E5.92.8C-report
    if (aegis) {
      aegis.report({
        msg: '接口请求信息',
        level: Aegis.logType.EVENT, // 日志等级，具体取值可参考以上文档链接。8192 表示自定义时间
        ext1: JSON.stringify(ext1),
        ext2: JSON.stringify(ext2),
        trace: 'trace', // 文档未有明确解释，可能是请求上报增加 trace header
      });
    }

    // 上报事件
    posthog.capture('api', {
      url,
      requestParams: options?.params,
      requestData: options?.data,
      ...Object.fromEntries(
        Object.entries(options ?? {}).filter(([k]) => !['params', 'data', 'notice'].includes(k)),
      ),

      responseSuccess: response?.success,
      responseInfo: response?.info,
      responseData: JSON.stringify(
        Object.fromEntries(
          Object.entries(response ?? {}).filter(([k]) => !['success', 'info', 'code'].includes(k)),
        ),
      ),
    });

    if (response && typeof response === 'object') {
      if (getResponse) {
        return [response, null];
      }

      const { success, data, info } = response;

      if (success) {
        return [isCamel ? camelCase(data) : data, null];
      }

      const errorMsg = info ?? defaultError;
      const errorRes = data ? (isCamel ? camelCase(data) : data) : errorMsg;

      // 200 但info是 账户未登录，请重新登录
      if (['未登录', '账户未登录，请重新登录'].includes(info) && !noLoginRequired()) {
        history.replace({
          pathname: '/user/login',
          search: stringify({ redirect: window.location.href }),
        });
      } else {
        if (notice) {
          const loginWithoutCode = !data?.need_double_check;
          // 200 但info是 账户未登录，请重新登录。后续可配置其他字段
          if (loginWithoutCode) onErrorNotice(notice, errorMsg, undefined, defaultError);
        }
      }

      return [null, errorRes];
    }

    return [response, null];
  } catch (error: unknown) {
    if (notice) {
      onErrorNotice(notice || url.split('/api/')[1], error, undefined, defaultError);
    }
    return [null, getErrorMsg(error, defaultError)];
  }
};

export default request;
